import { ClickDrag } from 'components/ui-blocks/click-drag-adjust';
import type {
  ClickDragReturn,
  Range,
} from 'components/ui-blocks/click-drag-adjust/ClickDragAdjust';
import type { DateTime } from 'luxon';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getTimelineGutterHighlight } from 'redux/selectors/timeline';
import type { MetaHourList } from 'types/timeline';
import { CursorOnTimelineIndicator } from './CursorOnTimelineIndicator';
import styles from './TimelineGutter.module.scss';

interface TimelineGutterProps {
  timelineHourHeightPx: number;
  timelineDayHeightPx: number;
  offsetTopPx: number;
  hoursList: MetaHourList;
  snapToMinutes: number;
  timelineWidthPx: number;
  timeFormat: 12 | 24;
  startOfDay: DateTime<true>;
  parentSelector: string;
  onSelect: (range: Range) => void;
  pixelsPerMinute: number;
  parentRef: {
    current: HTMLElement | null;
  };
  hideIndicators?: boolean;
}

export const TimelineGutter = ({
  timelineHourHeightPx,
  timelineDayHeightPx,
  offsetTopPx,
  hoursList,
  snapToMinutes,
  timelineWidthPx,
  timeFormat,
  parentRef,
  pixelsPerMinute,
  parentSelector,
  startOfDay,
  onSelect,
  hideIndicators,
}: TimelineGutterProps) => {
  const gutterHighlight = useSelector(getTimelineGutterHighlight);
  const isParentRefReady = !!parentRef && !!parentRef.current;

  const handleClickDragEnd = useCallback(
    (payload: ClickDragReturn) => {
      const { selectionFromY, selectionToY } = payload;
      const adjustedFromPxToMinute = selectionFromY / pixelsPerMinute;
      const adjustedToPxToMinute = selectionToY / pixelsPerMinute;
      const from = startOfDay.plus({ minutes: adjustedFromPxToMinute }).startOf('minute').toISO();
      const to = startOfDay.plus({ minutes: adjustedToPxToMinute }).startOf('minute').toISO();

      onSelect({
        index: null,
        from,
        to,
      });
    },
    [pixelsPerMinute, onSelect, startOfDay]
  );

  return (
    <>
      {gutterHighlight && <div className={styles.gutterHighlight} style={gutterHighlight}></div>}

      {isParentRefReady && (
        <ClickDrag
          parentSelector={parentSelector}
          parentRef={parentRef}
          offsetTopPx={offsetTopPx}
          offsetLeftPx={10}
          selectionWidthPx={null}
          onChange={handleClickDragEnd}
          snapToMinutes={snapToMinutes}
          pixelsPerMinute={pixelsPerMinute}
        />
      )}

      <div
        className={styles.hourLabelLane}
        style={{ minHeight: timelineDayHeightPx + offsetTopPx }}
      >
        {hoursList.map(({ hour, label }, index) => {
          // First hour has added padding on top, so account for that:
          const minHeight = index === 0 ? timelineHourHeightPx + offsetTopPx : timelineHourHeightPx;

          return (
            <div key={hour} style={{ minHeight }}>
              <span>{label}</span>
            </div>
          );
        })}
      </div>

      {!hideIndicators && (
        <CursorOnTimelineIndicator
          snapToMinutes={snapToMinutes}
          offsetLeftPx={48}
          indicatorWidthPx={timelineWidthPx}
          timeFormat={timeFormat}
          parentRef={parentRef}
          pixelsPerMinute={pixelsPerMinute}
          offsetTopPx={offsetTopPx}
        />
      )}
    </>
  );
};
